import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  FaFileAlt,
  FaFileExcel,
  FaFileImage,
  FaFilePdf,
  FaFilePowerpoint,
  FaFileVideo,
  FaFileWord,
  FaPlay,
  FaPlus,
} from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import axiosInstance from "../../axiosInstance";

const MAX_BYTES = 100 * 1024 * 1024;

const savedProductId = (productId) => {
  const id = Number(productId);
  return Number.isFinite(id) && id > 0 ? id : null;
};

const driveVisualKind = (file) => {
  const mime = (file?.mimeType || "").toLowerCase();
  const name = (file?.name || "").toLowerCase();
  if (mime.startsWith("image/") || /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(name)) return "image";
  if (mime.startsWith("video/") || /\.(mp4|webm|mov|mkv|avi)$/.test(name)) return "video";
  if (mime.includes("pdf") || name.endsWith(".pdf")) return "pdf";
  if (mime.includes("spreadsheet") || mime.includes("excel") || mime.includes("csv") || /\.(xlsx?|csv)$/.test(name)) {
    return "sheet";
  }
  if (mime.includes("presentation") || mime.includes("powerpoint") || /\.pptx?$/.test(name)) return "slides";
  if (mime.includes("document") || mime.includes("word") || mime.includes("msword") || /\.docx?$/.test(name)) {
    return "doc";
  }
  return "file";
};

const thumbnailUrl = (driveFileId, size) =>
  `https://drive.google.com/thumbnail?id=${encodeURIComponent(driveFileId)}&sz=${size}`;

const previewUrl = (driveFileId) =>
  `https://drive.google.com/file/d/${encodeURIComponent(driveFileId)}/preview`;

const FileTypeIcon = ({ kind }) => {
  const common = { size: 40, "aria-hidden": true };
  if (kind === "pdf") return <FaFilePdf {...common} color="#d32f2f" />;
  if (kind === "doc") return <FaFileWord {...common} color="#1565c0" />;
  if (kind === "sheet") return <FaFileExcel {...common} color="#2e7d32" />;
  if (kind === "slides") return <FaFilePowerpoint {...common} color="#e65100" />;
  if (kind === "image") return <FaFileImage {...common} color="#6a1b9a" />;
  if (kind === "video") return <FaFileVideo {...common} color="#4527a0" />;
  return <FaFileAlt {...common} color="#546e7a" />;
};

const ProductDrivePanel = ({ productId, readOnly }) => {
  const savedId = savedProductId(productId);
  const fileInputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [thumbFailed, setThumbFailed] = useState({});
  const [preview, setPreview] = useState(null);
  /** File đang tải lên (hiện ô placeholder có spinner). */
  const [uploading, setUploading] = useState([]);
  /** id tài liệu đang xóa (hiện overlay spinner trên ô đó). */
  const [removingIds, setRemovingIds] = useState({});

  useEffect(() => {
    if (!savedId) {
      setFiles([]);
      setError("");
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const response = await axiosInstance.get("/api/products/driveFiles", {
          params: { productId: savedId },
        });
        if (cancelled) return;
        if (response.data?.success === false) {
          setFiles([]);
          setError(response.data?.message || "Không tải được danh sách tài liệu");
          return;
        }
        setFiles(response.data?.data || []);
      } catch {
        if (!cancelled) {
          setFiles([]);
          setError("Không tải được danh sách tài liệu");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [savedId]);

  const remember = (row) => {
    if (!row?.id) return;
    setFiles((current) => [row, ...current.filter((item) => item.id !== row.id && item.driveFileId !== row.driveFileId)]);
  };

  const uploadFiles = async (event) => {
    const selected = Array.from(event.target.files || []);
    event.target.value = "";
    if (!selected.length || !savedId) return;
    const valid = selected.filter((file) => {
      if (file.size > MAX_BYTES) {
        toast.error(`${file.name} vượt quá 100MB`);
        return false;
      }
      return true;
    });
    if (!valid.length) return;
    const pending = valid.map((file, index) => ({ key: `${Date.now()}-${index}`, name: file.name, file }));
    setUploading((current) => [...current, ...pending.map(({ key, name }) => ({ key, name }))]);
    setBusy(true);
    let uploaded = 0;
    try {
      for (const { key, file } of pending) {
        const body = new FormData();
        body.append("productId", String(savedId));
        body.append("file", file);
        try {
          const response = await axiosInstance.post("/api/products/uploadDriveFile", body);
          if (response.data?.success === false) {
            toast.error(response.data?.message || `Không tải được ${file.name}`);
            continue;
          }
          remember(response.data?.data);
          uploaded += 1;
        } catch (err) {
          toast.error(err?.response?.data?.message || `Không tải được ${file.name}`);
        } finally {
          setUploading((current) => current.filter((item) => item.key !== key));
        }
      }
      if (uploaded > 0) {
        toast.success(uploaded === 1 ? "Đã tải file lên Google Drive" : `Đã tải ${uploaded} file lên Google Drive`);
      }
    } finally {
      setBusy(false);
    }
  };

  const removeFile = async (file) => {
    const question = file.source === "UPLOAD"
      ? `Xóa "${file.name}" trên Google Drive?`
      : `Gỡ "${file.name}" khỏi sản phẩm?`;
    if (!window.confirm(question)) return;
    setBusy(true);
    setRemovingIds((current) => ({ ...current, [file.id]: true }));
    try {
      const response = await axiosInstance.post("/api/products/removeDriveFile", null, {
        params: { id: file.id },
      });
      if (response.data?.success === false) {
        toast.error(response.data?.message || "Không xóa được tài liệu");
        return;
      }
      setFiles((current) => current.filter((item) => item.id !== file.id));
    } catch (err) {
      toast.error(err?.response?.data?.message || "Không xóa được tài liệu");
    } finally {
      setRemovingIds((current) => {
        const next = { ...current };
        delete next[file.id];
        return next;
      });
      setBusy(false);
    }
  };

  const openFile = (file) => {
    const kind = driveVisualKind(file);
    if (kind === "image" || kind === "video") {
      setPreview({ file, kind, imageFailed: false });
      return;
    }
    if (file.webViewLink) {
      window.open(file.webViewLink, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div className="group-box mt-2 mb-2" data-text="Tài liệu">
      {!savedId && (
        <small className="fst-italic text-muted">Lưu sản phẩm trước khi thêm tài liệu</small>
      )}
      {savedId && !readOnly && (
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="d-none"
          onChange={uploadFiles}
        />
      )}
      {loading && <small className="text-muted">Đang tải danh sách...</small>}
      {error && <div className="text-danger small">{error}</div>}
      {savedId && !loading && !error && files.length === 0 && readOnly && (
        <small className="fst-italic text-muted">Chưa có tài liệu</small>
      )}
      {savedId && !loading && (files.length > 0 || !readOnly) && (
        <div className="d-flex flex-wrap gap-3">
          {!readOnly && (
            <button
              type="button"
              className="drive-add-btn"
              disabled={busy}
              aria-label="Tải file lên Google Drive"
              title="Tải lên Google Drive"
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading.length > 0 ? (
                <span className="spinner-border" role="status" aria-hidden="true" />
              ) : (
                <FaPlus size={28} aria-hidden="true" />
              )}
            </button>
          )}
          {uploading.map((item) => (
            <div key={item.key} className="position-relative" style={{ width: 120 }} title={item.name}>
              <div
                className="border rounded bg-light d-flex flex-column align-items-center justify-content-center p-1"
                style={{ height: 108 }}
              >
                <span className="spinner-border text-primary" role="status" aria-hidden="true" />
                <span className="small text-muted mt-2">Đang tải lên...</span>
                <span className="small text-truncate w-100 text-center">{item.name}</span>
              </div>
            </div>
          ))}
          {files.map((file) => {
            const kind = driveVisualKind(file);
            const showThumb = (kind === "image" || kind === "video") && file.driveFileId && !thumbFailed[file.id];
            const removing = Boolean(removingIds[file.id]);
            return (
              <div key={file.id} className="position-relative drive-file-item" style={{ width: 120 }}>
                <button
                  type="button"
                  className="btn btn-light border w-100 d-flex flex-column align-items-center justify-content-center p-1"
                  style={{ height: 108 }}
                  title={file.name || "Tài liệu"}
                  disabled={removing}
                  onClick={() => openFile(file)}
                >
                  <span className="position-relative d-flex align-items-center justify-content-center" style={{ width: 72, height: 72 }}>
                    {showThumb ? (
                      <img
                        src={thumbnailUrl(file.driveFileId, "w400")}
                        alt=""
                        style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 8 }}
                        onError={() => setThumbFailed((current) => ({ ...current, [file.id]: true }))}
                      />
                    ) : (
                      <FileTypeIcon kind={kind} />
                    )}
                    {kind === "video" && showThumb && (
                      <FaPlay
                        aria-hidden="true"
                        color="#fff"
                        size={16}
                        className="position-absolute"
                        style={{ filter: "drop-shadow(0 0 2px rgba(0,0,0,.7))" }}
                      />
                    )}
                  </span>
                  <span className="small text-truncate w-100 mt-1">{file.name || "Tài liệu"}</span>
                </button>
                {removing && (
                  <div className="drive-file-loading" role="status">
                    <span className="spinner-border spinner-border-sm text-danger" aria-hidden="true" />
                    <span className="small mt-1">Đang xóa...</span>
                  </div>
                )}
                {!readOnly && !removing && (
                  <button
                    type="button"
                    className="btn btn-sm btn-light border position-absolute top-0 end-0 bg-danger text-white drive-file-remove"
                    style={{ width: 24, height: 24, padding: 0, transform: "translate(30%, -30%)" }}
                    aria-label={`Xóa ${file.name || "tài liệu"}`}
                    disabled={busy}
                    onClick={() => removeFile(file)}
                  >
                    <RiDeleteBin6Line size={12} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Modal show={preview != null} onHide={() => setPreview(null)} size="lg" centered style={{ zIndex: 2000 }}>
        <Modal.Header closeButton>
          <Modal.Title className="text-truncate">{preview?.file?.name || "Tài liệu"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {preview?.kind === "image" && !preview.imageFailed && (
            <img
              src={thumbnailUrl(preview.file.driveFileId, "w1600")}
              alt={preview.file.name || "Ảnh"}
              style={{ width: "100%", height: "auto", maxHeight: "70vh", objectFit: "contain" }}
              onError={() => setPreview((current) => (current ? { ...current, imageFailed: true } : current))}
            />
          )}
          {preview && (preview.kind === "video" || preview.imageFailed) && (
            <div className="ratio ratio-16x9">
              <iframe
                src={previewUrl(preview.file.driveFileId)}
                title={preview.file.name || "Xem trên Google Drive"}
                allow="autoplay; fullscreen"
                allowFullScreen
              />
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ProductDrivePanel;
